import type { FormEvent } from 'react'
import { Search } from 'lucide-react'

interface CoordInputProps {
  /** Decimal latitude as a string (keeps trailing/partial input intact). */
  lat: string
  onLatChange: (next: string) => void
  /** Decimal longitude as a string. */
  lon: string
  onLonChange: (next: string) => void
  /** Fires when the user submits the form (clicks Search or hits Enter). */
  onSearch: () => void
}

// Narrower inputs — never more than ~6 digits of decimal precision in
// practice (lon "-110.123456" = 11 chars at 8px/char ≈ 88px).
const INPUT_BASE_CLASS = [
  'w-[90px]',
  'bg-transparent',
  'font-[var(--font-mono)] tabular-nums text-[13.5px] font-medium',
  'text-[var(--color-text)]',
  'placeholder:text-[var(--color-muted)]',
  'focus:outline-none',
].join(' ')

const LABEL_CLASS =
  'font-[var(--font-sans)] text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted)]'

export function CoordInput({
  lat,
  onLatChange,
  lon,
  onLonChange,
  onSearch,
}: CoordInputProps) {
  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSearch()
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      aria-label="Resolve a coordinate"
      className={[
        // Anchored tight to the top-left of the map. Matches the basemap
        // toggle's top-right offset for visual symmetry. h-11 = 44 px keeps
        // every floating chrome element on the map at the same height.
        'absolute left-3 top-3 z-20',
        'flex items-center gap-2 sm:gap-3 h-11',
        // Right padding (pr-1 = 4 px) matches the 4 px implicit margin above
        // and below the inner Search button (button h-9 inside card h-11
        // → 2px ✕ 2 + the gap = 4 px) so the button has equal breathing
        // room on all four sides.
        'pl-3 pr-1',
        'rounded-[var(--radius-lg)]',
        'bg-[var(--color-elev-strong)] backdrop-blur-[14px]',
        'border border-[var(--color-border)]',
        'text-[var(--color-text)]',
        'shadow-[var(--shadow-coord)]',
        'transition-colors duration-200',
      ].join(' ')}
    >
      <label className="flex flex-col items-start leading-tight px-1.5">
        <span className={LABEL_CLASS}>Latitude</span>
        <input
          type="text"
          inputMode="decimal"
          value={lat}
          onChange={(e) => onLatChange(e.target.value)}
          placeholder="32.1941"
          aria-label="Latitude"
          spellCheck={false}
          autoComplete="off"
          className={INPUT_BASE_CLASS}
        />
      </label>

      <span aria-hidden className="h-7 w-px bg-[var(--color-border)]" />

      <label className="flex flex-col items-start leading-tight px-1.5">
        <span className={LABEL_CLASS}>Longitude</span>
        <input
          type="text"
          inputMode="decimal"
          value={lon}
          onChange={(e) => onLonChange(e.target.value)}
          placeholder="-110.8955"
          aria-label="Longitude"
          spellCheck={false}
          autoComplete="off"
          className={INPUT_BASE_CLASS}
        />
      </label>

      <button
        type="submit"
        // The visible label is hidden below sm, which leaves an icon-only
        // button with an aria-hidden glyph and no accessible name at all.
        // This is the name at every width; the visible text is decorative.
        aria-label="Search"
        className={[
          'inline-flex items-center gap-1.5 shrink-0 cursor-pointer',
          // Match the outer card's --radius-lg so the button nests like a
          // pill-within-a-pill instead of a hard-edged rectangle inside
          // the rounded card.
          'h-9 px-4 rounded-[var(--radius-lg)]',
          'bg-[var(--color-accent)] text-[var(--color-surface)]',
          'text-[13px] font-medium',
          'hover:bg-[var(--color-accent-hover)] transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
        ].join(' ')}
      >
        <Search size={14} aria-hidden />
        {/* Hide the "Search" label below sm so the icon-only button
            doesn't crowd the lat/lon inputs on narrow viewports. */}
        <span className="hidden sm:inline">Search</span>
      </button>
    </form>
  )
}
